import { Check } from 'office-ui-fabric-react/lib/components/Check';
import { toggleSelectAllRows } from 'owa-mail-actions';

import { MailListFilter } from 'owa-mail-list-filter-view';
import { TableView, getIsEverythingSelectedInTable } from 'owa-mail-list-store';
import ViewFilter from 'owa-service/lib/contract/ViewFilter';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import ReadingPanePosition from 'owa-session-store/lib/store/schema/ReadingPanePosition';

import * as React from 'react';
import { observer } from 'mobx-react';
import HeaderButtonData from '../../models/HeaderButtonData';
import { ActionButton } from 'office-ui-fabric-react/lib/Button';
import {
    personaCardBehavior,
    PersonaCardBehaviorProps,
} from 'owa-persona/lib/components/PersonaCardBehavior';

const styles = require('./HeaderActions.scss');
const classNames = require('classnames/bind').bind(styles);

interface Props {
    tableView: TableView;
    hideCheckAll: boolean;
    hideFilter: boolean;
    actionButtonData: HeaderButtonData[];
    isEVHeader?: boolean;
    headerActionsStylesAsPerUserSettings?: string;
}

function headerActions(props: Props): JSX.Element {
    const viewFiltersForMailFolderNode: ViewFilter[] = [
        'All',
        'Unread',
        'ToOrCcMe',
        'Flagged',
        'Mentioned',
        'HasAttachment',
    ];

    return props.isEVHeader
        ? getEVActions(props, viewFiltersForMailFolderNode)
        : getActions(props, viewFiltersForMailFolderNode);
}

function getActions(props: Props, viewFiltersForMailFolderNode: ViewFilter[]): JSX.Element {
    const controlsContainerClassNames = classNames(
        styles.controlsContainer,
        props.hideCheckAll && styles.noCheckbox
    );

    const buttonWrapperClassName = props.hideCheckAll
        ? styles.wideButtonsWrapper
        : styles.buttonsWrapper;

    return (
        <div className={controlsContainerClassNames}>
            {!props.hideCheckAll &&
                renderCheckAll(props.tableView, false, props.headerActionsStylesAsPerUserSettings)}
            <div className={buttonWrapperClassName}>
                {props.actionButtonData.map((data, idx) =>
                    getButtonForData(data, idx === props.actionButtonData.length - 1)
                )}
            </div>
            {props.hideFilter ? null : (
                <div className={styles.filterWrapper}>
                    <MailListFilter
                        supportedViewFilters={viewFiltersForMailFolderNode}
                        containerClassName={styles.mailFilterContainer}
                        filterMenuSource={'PeopleFilterMenu'}
                        tableViewId={props.tableView.id}
                    />
                </div>
            )}
        </div>
    );
}

function getEVActions(props: Props, viewFiltersForMailFolderNode: ViewFilter[]): JSX.Element {
    const isListViewEmpty = props.tableView && !props.tableView.rowKeys.length;
    const checkAllComponent =
        !props.hideCheckAll &&
        renderCheckAll(props.tableView, true, props.headerActionsStylesAsPerUserSettings);
    const mailListFilterComponent = !props.hideFilter && (
        <div className={styles.filterWrapper}>
            <MailListFilter
                supportedViewFilters={viewFiltersForMailFolderNode}
                containerClassName={styles.mailFilterContainer}
                filterMenuSource={'PeopleFilterMenu'}
                tableViewId={props.tableView.id}
            />
        </div>
    );

    const displaySecondRow = (checkAllComponent && !isListViewEmpty) || mailListFilterComponent;

    return (
        <div className={styles.controlsContainerV2}>
            <div className={styles.buttonsWrapperV2}>
                {props.actionButtonData.map((data, idx) =>
                    getButtonForData(data, idx === props.actionButtonData.length - 1)
                )}
            </div>
            {displaySecondRow && (
                <div className={styles.secondRowContainer}>
                    {checkAllComponent}
                    {mailListFilterComponent}
                </div>
            )}
        </div>
    );
}

function renderCheckAll(
    tableView: TableView,
    isEVHeader: boolean,
    headerActionsStylesAsPerUserSettings: string
): JSX.Element {
    // PersonaHeader could be loaded independent of list view in PhotoHub,
    // so we need to check if tableView is null
    const isInCheckedMode = tableView && tableView.isInCheckedMode;
    const isEverythingSelected = getIsEverythingSelectedInTable(tableView);
    const isListViewEmpty = tableView && !tableView.rowKeys.length;
    const isReadingPanePositionRight =
        getUserConfiguration().UserOptions.GlobalReadingPanePositionReact ===
        ReadingPanePosition.Right;
    let checkBoxStyles = isEVHeader ? styles.checkBoxSingleLineEV : styles.checkBoxSingleLine;
    let checkBoxClassNames;

    let checkBoxThreeColumnStyles = isEVHeader
        ? styles.checkBoxThreeColumnEV
        : styles.checkBoxThreeColumn;

    checkBoxClassNames = classNames(
        headerActionsStylesAsPerUserSettings,
        isReadingPanePositionRight ? checkBoxThreeColumnStyles : checkBoxStyles,
        isInCheckedMode && styles.showCheckbox,
        !isListViewEmpty && styles.listViewHasItems
    );

    return (
        <div
            className={checkBoxClassNames}
            onClick={isListViewEmpty ? null : () => toggleSelectAllRows(tableView)}>
            {<Check checked={isEverythingSelected} />}
        </div>
    );
}

function getButtonForData(actionButtonData: HeaderButtonData, isLastButton: boolean): JSX.Element {
    const buttonStyles = {
        root: isLastButton ? styles.lastButton : styles.button,
        label: isLastButton ? styles.lastLabelContainer : '',
        textContainer: isLastButton ? styles.lastTextContainer : '',
    };

    if (actionButtonData.isLPCWrapped) {
        return (
            <LPCWrapper
                key={actionButtonData.personaEmail}
                emailAddress={actionButtonData.personaEmail}
                name={actionButtonData.personaName}
                locationToOpen={actionButtonData.lpcLocationToOpen}
                clientScenario={actionButtonData.lpcClientScenario}
                className={isLastButton ? styles.lastLpcWrapperClass : ''}>
                <ActionButton
                    iconProps={{
                        iconName: actionButtonData.icon,
                    }}
                    styles={buttonStyles}
                    text={actionButtonData.text}
                />
            </LPCWrapper>
        );
    } else {
        return (
            <ActionButton
                key={actionButtonData.text}
                iconProps={{
                    iconName: actionButtonData.icon,
                }}
                styles={buttonStyles}
                text={actionButtonData.text}
                onClick={actionButtonData.clickCallback}
            />
        );
    }
}

@personaCardBehavior
class LPCWrapper extends React.Component<PersonaCardBehaviorProps, React.ComponentState> {
    render(): any {
        return <div>{this.props.children}</div>;
    }
}

export default observer(headerActions);
